/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters */
import { formatISO } from 'date-fns';
import { dateRangeMax } from '../../functions/max.function';
import { dateRangeMin } from '../../functions/min.function';
import { DateRangePipeOptions } from '../../pipes/tt-date-range/date-range-pipe-options.interface';
import { TtDateRangePipe } from '../../pipes/tt-date-range/tt-date-range.pipe';
import { BaseDateRange } from '../base-date-range.model';
import { PlainRangeInterface } from '../range.interface';
import { DateRangeInterface } from './date-range.interface';

export class DateRange<
  StartBoundary extends 'finite' | 'infinite' = 'finite',
  EndBoundary extends 'finite' | 'infinite' = 'finite',
  RangeType extends Date = Date,
  PlainType extends string | number = string,
  StartType extends RangeType | null = StartBoundary extends 'finite'
    ? RangeType
    : RangeType | null,
  EndType extends RangeType | null = EndBoundary extends 'finite' ? RangeType : RangeType | null,
  PlainStartType extends PlainType | null = StartBoundary extends 'finite'
    ? PlainType
    : PlainType | null,
  PlainEndType extends PlainType | null = EndBoundary extends 'finite'
    ? PlainType
    : PlainType | null,
> extends BaseDateRange<StartBoundary, EndBoundary, RangeType, PlainType, StartType, EndType> {
  /**
   * @inheritDoc
   */
  public override get min(): StartType {
    return dateRangeMin<
      StartBoundary,
      EndBoundary,
      PlainType,
      PlainType | null,
      PlainType | null,
      StartType
    >(this.toObject());
  }

  /**
   * @inheritDoc
   */
  public override get max(): EndType {
    return dateRangeMax<
      StartBoundary,
      EndBoundary,
      PlainType,
      PlainType | null,
      PlainType | null,
      EndType
    >(this.toObject());
  }

  /**
   * @inheritDoc
   */
  public static override fromObject<
    PlainType extends string | number,
    RangeType extends Date | string | number,
    D extends
      | PlainRangeInterface<'finite' | 'infinite', 'finite' | 'infinite', PlainType>
      | DateRangeInterface<'finite' | 'infinite', 'finite' | 'infinite', RangeType>,
    StartBoundary extends 'finite' | 'infinite' = D['start'] extends null
      ? 'infinite'
      : D['start'] extends string | number
        ? 'finite'
        : 'infinite',
    EndBoundary extends 'finite' | 'infinite' = D['end'] extends null
      ? 'infinite'
      : D['end'] extends string | number
        ? 'finite'
        : 'infinite',
    StartType extends StartBoundary extends 'finite' ? Date : null | Date =
      StartBoundary extends 'finite' ? Date : null | Date,
    EndType extends EndBoundary extends 'finite' ? Date : null | Date = EndBoundary extends 'finite'
      ? Date
      : null | Date,
  >(data: D) {
    return new DateRange<StartBoundary, EndBoundary, Date, PlainType>(
      (data.start === null ? null : new Date(data.start)) as StartType,
      (data.end === null ? null : new Date(data.end)) as EndType,
      data.inclusiveStart,
      data.inclusiveEnd,
    );
  }

  /**
   * @inheritDoc
   */
  public override toObject(): PlainRangeInterface<
    StartBoundary,
    EndBoundary,
    PlainType,
    PlainStartType,
    PlainEndType
  > {
    return {
      start: (this.start
        ? formatISO(this.start as unknown as Date, { representation: 'date' })
        : null) as PlainStartType,
      end: (this.end
        ? formatISO(this.end as unknown as Date, { representation: 'date' })
        : null) as PlainEndType,
      inclusiveStart: this.inclusiveStart,
      inclusiveEnd: this.inclusiveEnd,
    };
  }

  /**
   * @inheritDoc
   */
  public override humanReadableString(
    divider: string = ' - ',
    formatString: string = 'eee d-LLL',
    options?: DateRangePipeOptions,
  ): string {
    return new TtDateRangePipe().transform(this, {
      ...(options ?? {}),
      divider: divider,
      format: formatString,
    });
  }

  /**
   * @inheritDoc
   */
  public override toString(): string {
    const parts: string[] = [];
    parts.push(this.start === null ? '(' : this.inclusiveStart ? '[' : '(');
    // The lower bound of a range can be omitted, meaning that all values less
    // than the upper bound are included in the range, e.g., (,3].
    parts.push(this.start ? formatISO(this.start, { representation: 'date' }) : '');
    parts.push(',');
    // Likewise, if the upper bound of the range is omitted, then all values
    // greater than the lower bound are included in the range
    parts.push(this.end ? formatISO(this.end, { representation: 'date' }) : '');
    parts.push(this.end === null ? ')' : this.inclusiveEnd ? ']' : ')');
    return parts.join('');
  }
}
